'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { ArrowUpRight, Loader2 } from 'lucide-react'
import api from '@/lib/api'
import { VelovaIcon, VelovaLogo } from '@/components/velova-logo'
import { SITE_META } from '@/lib/site-config'
import { fireAnalyticsEvent } from '@/lib/analytics'

const SIGNUP_STEPS = [
  {
    index: '01',
    title: 'Add the brand',
    description: 'Set the market, language and closest competitors.',
    block: 'velova-block-blue',
  },
  {
    index: '02',
    title: 'Build the questions',
    description: 'Review discovery, comparison and trust prompts.',
    block: 'velova-block-mint',
  },
  {
    index: '03',
    title: 'Start the loop',
    description: 'Monitor the answers and open the first prioritized actions.',
    block: 'velova-block-amber',
  },
] as const

export default function SignupPage() {
  const router = useRouter()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const startedRef = useRef(false)

  const handleFormFocus = () => {
    if (startedRef.current) return
    startedRef.current = true
    fireAnalyticsEvent('signup_started')
  }

  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const err = params.get('error')
    if (err) {
      setError(err)
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const handleGoogleSignIn = async () => {
    setError('')
    setGoogleLoading(true)
    try {
      const response = await api.get('/auth/google/start')
      if (response.data?.authUrl) {
        window.location.href = response.data.authUrl
      } else {
        throw new Error('No auth URL returned')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Google sign-in failed')
      setGoogleLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)

    try {
      const response = await api.post('/auth/signup', { name, email, password })
      localStorage.setItem('token', response.data.token)
      localStorage.setItem('user', JSON.stringify(response.data.user))
      fireAnalyticsEvent('signup_completed')
      router.push('/companies/new')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create account')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="old-landing velova-canvas velova-grain min-h-screen flex">
      {/* Left panel — editorial showcase */}
      <aside className="hidden lg:flex lg:w-[52%] relative overflow-hidden border-r border-[var(--velova-line-strong)]">
        <div className="absolute inset-0 velova-brush opacity-80 pointer-events-none" />

        <div className="relative w-full flex flex-col justify-between p-12 xl:p-16">
          <div className="flex items-center justify-between">
            <Link href="/" className="flex items-center gap-3 group">
              <VelovaLogo variant="light" />
            </Link>
            <span className="velova-mono-label opacity-60">§ Register</span>
          </div>

          <div className="max-w-[560px]">
            <div className="velova-mono-label opacity-60 mb-6">
              Start with the buyer question
            </div>

            <h1 className="velova-display text-[52px] xl:text-[64px] leading-[1.02]">
              See what AI says about your brand.{' '}
              <span className="velova-italic" style={{ color: 'var(--velova-blue-deep)' }}>
                Then run what happens next.
              </span>
            </h1>

            {/* Onboarding steps as editorial blocks */}
            <div className="mt-10 space-y-3 max-w-md">
              {SIGNUP_STEPS.map((step) => (
                <div key={step.index} className={`velova-block ${step.block} flex gap-4 p-4`}>
                  <span className="velova-display text-[28px] leading-none opacity-50">
                    {step.index}
                  </span>
                  <div>
                    <div className="text-[14px] font-semibold">{step.title}</div>
                    <div className="text-[13px] opacity-70 mt-1 leading-snug">
                      {step.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[12px] opacity-60">— The Velova team</div>
        </div>
      </aside>

      {/* Right panel — form */}
      <section className="flex-1 flex flex-col">
        <div className="lg:hidden p-5 sm:p-8 flex items-center justify-between border-b border-[var(--velova-line-strong)]">
          <Link href="/" className="flex items-center gap-3">
            <VelovaLogo variant="light" width={100} />
          </Link>
          <span className="velova-mono-label opacity-60">§ Register</span>
        </div>

        <div className="flex-1 flex items-center justify-center p-5 sm:p-8 lg:p-16">
          <div className="w-full max-w-md">
            <div className="mb-8 sm:mb-10">
              <div className="velova-mono-label opacity-60 mb-4">Create workspace</div>
              <h2 className="velova-display text-[32px] sm:text-[40px] lg:text-[44px] leading-[0.95]">
                Create your{' '}
                <span className="velova-italic" style={{ color: 'var(--velova-blue-deep)' }}>
                  Velova
                </span>{' '}
                account.
              </h2>
              <p className="text-[14px] opacity-70 mt-4">
                Start free. Add the first brand after account creation.
              </p>
            </div>

            <form onSubmit={handleSubmit} onFocus={handleFormFocus} className="space-y-6">
              {error && (
                <div className="velova-block velova-block-rose p-4">
                  <div className="velova-mono-label mb-1">Error</div>
                  <div className="text-[13px] leading-tight">{error}</div>
                </div>
              )}

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || loading}
                className="flex w-full items-center justify-center gap-3 rounded-full border border-[var(--velova-line-strong)] bg-white/80 px-5 py-3 text-[14px] font-semibold text-slate-800 transition hover:bg-white disabled:opacity-60 dark:bg-white/[0.04] dark:text-white dark:hover:bg-white/[0.06]"
              >
                {googleLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <GoogleGlyph className="h-[18px] w-[18px]" />
                )}
                {googleLoading ? 'Redirecting to Google…' : 'Continue with Google'}
              </button>

              <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.22em] opacity-50">
                <span className="h-px flex-1 bg-[var(--velova-line-strong)]" />
                or
                <span className="h-px flex-1 bg-[var(--velova-line-strong)]" />
              </div>

              <FieldEditorial
                id="name"
                label="Full name"
                type="text"
                placeholder="Ada Lovelace"
                value={name}
                onChange={setName}
                disabled={loading}
                index="01"
                autoComplete="name"
              />

              <FieldEditorial
                id="email"
                label="Work email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={setEmail}
                disabled={loading}
                index="02"
                autoComplete="email"
              />

              <FieldEditorial
                id="password"
                label="Password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={setPassword}
                disabled={loading}
                index="03"
                autoComplete="new-password"
              />

              <FieldEditorial
                id="confirmPassword"
                label="Confirm password"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={setConfirmPassword}
                disabled={loading}
                index="04"
                autoComplete="new-password"
              />

              <button
                type="submit"
                disabled={loading}
                className="velova-btn velova-btn-ink w-full justify-center disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-[14px] h-[14px] animate-spin" />
                    Creating account
                  </>
                ) : (
                  <>
                    Create account
                    <ArrowUpRight className="w-[14px] h-[14px]" />
                  </>
                )}
              </button>

              <p className="text-[12px] leading-relaxed opacity-60">
                By creating an account, you acknowledge the Velova{' '}
                <Link href="/privacy" className="underline hover:opacity-70 transition-opacity">
                  Privacy Policy
                </Link>
                .
              </p>

              <div className="velova-hr" />

              <div className="flex items-center justify-between text-[13px]">
                <span className="opacity-70">Already have an account?</span>
                <Link
                  href="/login"
                  className="flex items-center gap-1.5 hover:opacity-70 transition-opacity"
                >
                  Sign in
                  <ArrowUpRight className="w-[12px] h-[12px]" />
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className="p-5 sm:p-8 flex items-center justify-between text-[11px] opacity-50">
          <span>{SITE_META.copyright}</span>
          <span className="velova-mono-label">v1.0</span>
        </div>
      </section>
    </main>
  )
}

function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.99 10.99 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  )
}

function FieldEditorial({
  id,
  label,
  type,
  placeholder,
  value,
  onChange,
  disabled,
  index,
  autoComplete,
}: {
  id: string
  label: string
  type: string
  placeholder: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  index: string
  autoComplete?: string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label htmlFor={id} className="velova-mono-label opacity-70">
          {label}
        </label>
        <span className="velova-mono-label opacity-40">§ {index}</span>
      </div>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required
        disabled={disabled}
        autoComplete={autoComplete}
        className="w-full bg-transparent border-0 border-b border-[var(--velova-line-strong)] pb-3 pt-1 text-[18px] focus:outline-none focus:border-[var(--velova-blue-deep)] transition-colors placeholder:opacity-40"
      />
    </div>
  )
}
